import React from "react";
import {
  Eye, Edit2, Check, X, Trash2, Building2, Shield, ShieldOff, Crown, KeyRound,
} from "lucide-react";
import { ROLE_LABELS } from "./constants";

export function UserTableRow({
  user, isRoot, organizations,
  actions,
}) {
  const roleInfo = ROLE_LABELS[user.role] || {
    label: user.role,
    color: "bg-slate-500/20 text-slate-400",
  };
  const {
    impersonating, editingRole, newRole, setNewRole,
    editingOrg, newOrgId, setNewOrgId,
    deletingUser, togglingSA,
    resetPasswordTarget,
    handleImpersonate,
    handleEditRole, handleSaveRole, handleCancelEdit,
    handleEditOrg, handleSaveOrg, handleCancelOrgEdit,
    handleDeleteUser, handleToggleSuperadmin,
    handleResetPassword,
  } = actions;

  return (
    <tr className="hover:bg-slate-800/30">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
            <span className="text-white font-medium">
              {user.name?.charAt(0).toUpperCase() || "U"}
            </span>
          </div>
          <div>
            <p className="text-white font-medium">{user.name}</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {editingOrg === user.user_id ? (
          <div className="flex items-center gap-2">
            <select
              value={newOrgId || ""}
              onChange={(e) => setNewOrgId(e.target.value)}
              data-testid={`edit-org-select-${user.user_id}`}
              className="text-xs px-2 py-1 bg-slate-800 border border-slate-700 rounded text-white max-w-[200px]"
            >
              {/* Placeholder when the user has no org yet — keeps the save
                  button disabled until the SA picks something real. */}
              {!newOrgId && (
                <option value="" disabled>— Selecciona una organización —</option>
              )}
              {organizations.map((org) => (
                <option key={org.organization_id} value={org.organization_id}>
                  {org.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => handleSaveOrg(user.user_id)}
              disabled={!newOrgId}
              data-testid={`save-org-btn-${user.user_id}`}
              className="p-1 text-emerald-400 hover:bg-emerald-500/10 rounded disabled:opacity-40 disabled:hover:bg-transparent"
              title="Guardar"
            >
              <Check size={14} />
            </button>
            <button
              onClick={handleCancelOrgEdit}
              data-testid={`cancel-org-btn-${user.user_id}`}
              className="p-1 text-slate-400 hover:bg-slate-700 rounded"
              title="Cancelar"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <p className="text-slate-300">{user.organization_name || "—"}</p>
            {/* Building icon = edit org. Hidden only for the immutable
                root superadmin; regular SAs CAN now have a tenant org
                (they keep full SA powers via role-only is_superadmin()). */}
            {!user.is_root_superadmin && (
              <button
                onClick={() => handleEditOrg(user.user_id, user.organization_id)}
                data-testid={`edit-org-btn-${user.user_id}`}
                className="p-1 text-slate-400 hover:text-teal-400 hover:bg-slate-700 rounded"
                title="Cambiar organización"
              >
                <Building2 size={14} />
              </button>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-slate-400 capitalize">
          {user.organization_plan || "—"}
        </span>
      </td>
      <td className="px-4 py-3">
        {editingRole === user.user_id ? (
          <div className="flex items-center gap-2">
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="text-xs px-2 py-1 bg-slate-800 border border-slate-700 rounded text-white"
            >
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
              <option value="compliance_officer">Cumplimiento</option>
              <option value="capturista">Capturista</option>
            </select>
            <button
              onClick={() => handleSaveRole(user.user_id)}
              className="p-1 text-emerald-400 hover:bg-emerald-500/10 rounded"
              title="Guardar"
            >
              <Check size={14} />
            </button>
            <button
              onClick={handleCancelEdit}
              className="p-1 text-red-400 hover:bg-red-500/10 rounded"
              title="Cancelar"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${roleInfo.color}`}>
              {roleInfo.label}
            </span>
            {user.role !== "superadmin" && (
              <button
                onClick={() => handleEditRole(user.user_id, user.role)}
                className="p-1 text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 rounded"
                title="Editar rol"
              >
                <Edit2 size={14} />
              </button>
            )}
          </div>
        )}
      </td>
      <td data-testid={`sa-cell-${user.user_id}`} className="px-4 py-3">
        {user.is_root_superadmin ? (
          <span
            data-testid={`sa-root-badge-${user.user_id}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold bg-rose-500/20 text-rose-300 border border-rose-500/30"
            title="Superadmin raíz — inmutable"
          >
            <Crown size={12} />
            Root
          </span>
        ) : user.role === "superadmin" ? (
          <button
            onClick={() => handleToggleSuperadmin(user)}
            disabled={!isRoot || togglingSA === user.user_id}
            data-testid={`sa-demote-btn-${user.user_id}`}
            title={isRoot ? "Quitar privilegios de superadmin" : "Solo el superadmin raíz puede modificar esto"}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ShieldOff size={12} />
            {togglingSA === user.user_id ? "..." : "Superadmin"}
          </button>
        ) : (
          <button
            onClick={() => handleToggleSuperadmin(user)}
            disabled={!isRoot || togglingSA === user.user_id}
            data-testid={`sa-promote-btn-${user.user_id}`}
            title={isRoot ? "Promover a superadmin" : "Solo el superadmin raíz puede otorgar este rol"}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium border border-slate-700 text-slate-400 hover:bg-emerald-500/10 hover:text-emerald-300 hover:border-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400 disabled:hover:border-slate-700 transition-colors"
          >
            <Shield size={12} />
            {togglingSA === user.user_id ? "..." : "Promover"}
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          {/* Ver como: shown for all non-superadmins, and for
              non-root superadmins when the caller is root
              (useful for debugging their panel view). */}
          {(user.role !== "superadmin" ||
            (isRoot && !user.is_root_superadmin)) && (
            <button
              onClick={() => handleImpersonate(user.user_id)}
              disabled={impersonating === user.user_id}
              title="Impersonar"
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-amber-400 hover:bg-amber-500/10 rounded-lg border border-amber-500/30"
            >
              <Eye size={14} />
              {impersonating === user.user_id ? "..." : "Ver como"}
            </button>
          )}
          {user.role !== "superadmin" && !user.is_root_superadmin && (
            <button
              onClick={() => handleDeleteUser(user.user_id, user.email)}
              disabled={deletingUser === user.user_id}
              title="Eliminar usuario"
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10 rounded-lg border border-red-500/30"
            >
              <Trash2 size={14} />
              {deletingUser === user.user_id ? "..." : "Eliminar"}
            </button>
          )}
          {/* Reset password — available for ALL users (including
              fellow superadmins). The backend audit log records who
              triggered what, and SAs are trusted operators. */}
          <button
            onClick={() => handleResetPassword(user)}
            disabled={resetPasswordTarget?.user_id === user.user_id}
            data-testid={`reset-password-btn-${user.user_id}`}
            title="Resetear contraseña — envía email al usuario"
            className="flex items-center gap-1 px-3 py-1.5 text-xs text-amber-300 hover:bg-amber-500/10 rounded-lg border border-amber-500/30 disabled:opacity-50"
          >
            <KeyRound size={14} />
            Resetear contraseña
          </button>
        </div>
      </td>
    </tr>
  );
}
